/**
 * Attachment Upload Component
 * Drag & drop file uploader for audit finding attachments
 * Supports: JPEG, PNG, PDF, DOCX, XLSX (max 10MB each)
 */

import { useState, useRef } from "react";
import {
  Paper,
  Group,
  Text,
  Button,
  Stack,
  TextInput,
  Progress,
  Alert,
  Badge,
} from "@mantine/core";
import {
  IconUpload,
  IconX,
  IconCheck,
  IconAlertCircle,
  IconPhoto,
  IconFileText,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useUploadAttachment } from "../../hooks/useAttachments";

interface AttachmentUploadProps {
  findingId: string;
  onUploadComplete?: () => void;
  disabled?: boolean;
}

const ALLOWED_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // DOCX
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // XLSX
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export function AttachmentUpload({
  findingId,
  onUploadComplete,
  disabled = false,
}: AttachmentUploadProps) {
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useUploadAttachment(findingId);

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return "Invalid file type. Allowed: JPEG, PNG, PDF, DOCX, XLSX";
    }
    if (file.size > MAX_FILE_SIZE) {
      return "File too large. Maximum size: 10MB";
    }
    return null;
  };

  const handleFileSelect = (file: File) => {
    const error = validateFile(file);
    if (error) {
      notifications.show({
        title: "Invalid File",
        message: error,
        color: "red",
        icon: <IconAlertCircle size={16} />,
      });
      return;
    }

    setSelectedFile(file);
    setUploadProgress(0);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    if (disabled || uploadMutation.isPending) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled && !uploadMutation.isPending) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleBrowseClick = () => {
    if (disabled || uploadMutation.isPending) return;
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      // Simulate progress (real progress tracking requires axios onUploadProgress)
      setUploadProgress(30);

      await uploadMutation.mutateAsync({
        file: selectedFile,
        description: description || undefined,
      });

      setUploadProgress(100);

      notifications.show({
        title: "Upload Successful",
        message: `${selectedFile.name} has been uploaded`,
        color: "green",
        icon: <IconCheck size={16} />,
      });

      // Reset state
      setSelectedFile(null);
      setDescription("");
      setUploadProgress(0);

      onUploadComplete?.();
    } catch {
      setUploadProgress(0);
      // error toast comes from the global mutation handler (US-081)
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setDescription("");
    setUploadProgress(0);
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith("image/")) {
      return <IconPhoto size={32} color="var(--mantine-color-blue-6)" />;
    }
    return <IconFileText size={32} color="var(--mantine-color-gray-6)" />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Stack gap="md">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_TYPES.join(",")}
        onChange={handleFileInputChange}
        style={{ display: "none" }}
      />

      {/* Drag & Drop Area */}
      {!selectedFile && (
        <Paper
          p="xl"
          withBorder
          style={{
            borderStyle: "dashed",
            borderWidth: 2,
            borderColor: dragOver
              ? "var(--mantine-color-blue-6)"
              : "var(--mantine-color-gray-4)",
            backgroundColor: dragOver
              ? "var(--mantine-color-blue-0)"
              : disabled
                ? "var(--mantine-color-gray-1)"
                : "transparent",
            cursor: disabled ? "not-allowed" : "pointer",
            transition: "all 0.2s",
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleBrowseClick}
        >
          <Stack align="center" gap="md">
            <IconUpload
              size={48}
              color={
                dragOver
                  ? "var(--mantine-color-blue-6)"
                  : "var(--mantine-color-gray-6)"
              }
            />
            <div>
              <Text size="lg" fw={600} ta="center">
                {dragOver ? "Drop file here" : "Drag & drop file here"}
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                or click to browse
              </Text>
            </div>
            <Group gap="xs">
              <Badge variant="light" color="gray">
                JPEG
              </Badge>
              <Badge variant="light" color="gray">
                PNG
              </Badge>
              <Badge variant="light" color="gray">
                PDF
              </Badge>
              <Badge variant="light" color="gray">
                DOCX
              </Badge>
              <Badge variant="light" color="gray">
                XLSX
              </Badge>
            </Group>
            <Text size="xs" c="dimmed">
              Maximum file size: 10MB
            </Text>
          </Stack>
        </Paper>
      )}

      {/* Selected File Preview */}
      {selectedFile && (
        <Paper p="md" withBorder>
          <Stack gap="md">
            <Group justify="space-between" align="flex-start">
              <Group gap="md">
                {getFileIcon(selectedFile.type)}
                <div>
                  <Text fw={600}>{selectedFile.name}</Text>
                  <Text size="sm" c="dimmed">
                    {formatFileSize(selectedFile.size)} • {selectedFile.type}
                  </Text>
                </div>
              </Group>
              {!uploadMutation.isPending && uploadProgress === 0 && (
                <Button
                  variant="subtle"
                  color="red"
                  size="sm"
                  onClick={handleCancel}
                  leftSection={<IconX size={16} />}
                >
                  Remove
                </Button>
              )}
            </Group>

            {/* Description Input */}
            {uploadProgress === 0 && (
              <TextInput
                label="Description (optional)"
                placeholder="Add a description for this attachment..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={uploadMutation.isPending}
              />
            )}

            {/* Upload Progress */}
            {uploadProgress > 0 && uploadProgress < 100 && (
              <div>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" fw={600}>
                    Uploading...
                  </Text>
                  <Text size="sm" c="dimmed">
                    {uploadProgress}%
                  </Text>
                </Group>
                <Progress value={uploadProgress} animated />
              </div>
            )}

            {/* Success Message */}
            {uploadProgress === 100 && (
              <Alert
                icon={<IconCheck size={16} />}
                color="green"
                title="Upload Complete"
              >
                File uploaded successfully
              </Alert>
            )}

            {/* Upload Button */}
            {uploadProgress === 0 && (
              <Group justify="flex-end">
                <Button
                  onClick={handleUpload}
                  loading={uploadMutation.isPending}
                  leftSection={<IconUpload size={16} />}
                >
                  Upload
                </Button>
              </Group>
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
